<template>
    <div>
        <b-card>
            <div class="d-flex flex-wrap justify-content-between mt-2">
                <div>
                    <button type="button" class="btn btn-primary mb-2 mr-2 float-left" @click="resetOrderData">
                        <i class="mdi mdi-arrow-left mr-1"></i> Go back
                    </button>
                </div>
                <div v-if="(loggedIn_user_role == 1 || loggedIn_user_role == 2) && hasMounted">
                    <b-button-group class="mb-2">
                        <b-button variant="secondary" :disabled="dispatchBusy" @click="printTag()">
                            <i class="mdi mdi-cloud-print mr-1"></i> Customer Sticker
                        </b-button>
                        <b-button class="ml-2" variant="secondary" :disabled="dispatchBusy" @click="printPDF('printCustomerLabel')">
                            <i class="mdi mdi-printer mr-1"></i> Customer Label
                        </b-button>
                        <b-button class="ml-2" variant="secondary" :disabled="dispatchBusy" @click="printPDF('printOrder')">
                            <i class="mdi mdi-printer mr-1"></i> Quality Control Form
                        </b-button>
                    </b-button-group>
                </div>
            </div>
        </b-card>

        <!-- Story 7.8: the dedicated Dispatch panel — the single operator command
             surface for the outbound chain (Direction B). Composes the shared
             primitives (DispatchAction + StatusPill + LabelPrintTrigger); never
             hand-rolls a confirm/spinner/badge and never calls axios. Status is
             DERIVED from ref presence (AC-2): there is no global `dispatched` flag,
             so a half-succeeded chain (shipped-not-invoiced / invoiced-not-shipped)
             is always visible and the missing half stays individually actionable
             (AC-3). All three controls sit inside one <fieldset> locked by
             dispatchBusy, so any in-progress push freezes its siblings — mirroring
             the server-side concurrency guard (AC-3 / UX-DR3). -->
        <b-card
            v-if="(loggedIn_user_role == 1 || loggedIn_user_role == 2) && hasMounted"
            class="dispatch-panel"
            title="Dispatch"
        >
            <fieldset class="dispatch-panel__controls" :disabled="dispatchBusy">
                <!-- Ship (BobGo): when shipped, DispatchAction shows the result badge
                     + waybill/track links and never the button (idempotency-as-state);
                     when not, the idle button IS the still-actionable affordance and a
                     "Not shipped" pending pill makes the half-state read at a glance. -->
                <div class="dispatch-panel__row">
                    <span class="dispatch-panel__step">Ship (BobGo)</span>
                    <StatusPill
                        v-if="!selected_order_details.bobgo_shipment_ref"
                        status="pending"
                        label="Not shipped"
                    />
                    <DispatchAction
                        dialogId="ship-via-bobgo"
                        actionLabel="Ship via BobGo"
                        confirmText="Ship this order via BobGo?"
                        :alreadyComplete="!!selected_order_details.bobgo_shipment_ref"
                        :resultBadge="{ status: 'done', label: 'Shipped' }"
                        :artifacts="shipArtifacts"
                        :action="() => shipViaBobGo(selected_order_details.id)"
                        @busy-change="dispatchBusy = $event"
                    />
                </div>

                <!-- Invoice (Sage): the new operator action (Stories 7.5-7.7 deferred
                     ALL Sage UI to here). sage_invoice_ref is a plain reference STRING,
                     not a link — surface it as text in the badge and pass artifacts=[]
                     (the artifacts prop is for {href,label} links only; do not fabricate
                     a Sage URL). -->
                <div class="dispatch-panel__row">
                    <span class="dispatch-panel__step">Invoice (Sage)</span>
                    <StatusPill
                        v-if="!selected_order_details.sage_invoice_ref"
                        status="pending"
                        label="Not invoiced"
                    />
                    <DispatchAction
                        dialogId="push-to-sage"
                        actionLabel="Push to Sage"
                        confirmText="Push this order's invoice to Sage?"
                        :alreadyComplete="!!selected_order_details.sage_invoice_ref"
                        :resultBadge="{ status: 'done', label: (selected_order_details.sage_invoice_ref || '').toString().trim() ? 'Invoiced · ' + selected_order_details.sage_invoice_ref : 'Invoiced' }"
                        :artifacts="[]"
                        :action="() => pushToSage(selected_order_details.id)"
                        @busy-change="dispatchBusy = $event"
                    />
                </div>

                <!-- Bulk label (Story 6.3): co-located so all three operator actions
                     live together. Shown ONLY when the order has >=1 bulk line item, so
                     there is never a dead control. The fieldset disables it during a push
                     (it has no own `disabled` prop — native fieldset locking covers it). -->
                <div v-if="hasBulkItems" class="dispatch-panel__row">
                    <span class="dispatch-panel__step">Bulk label</span>
                    <LabelPrintTrigger
                        variant="bulk"
                        @bulk-print="printBulkLabels"
                    />
                </div>
            </fieldset>

            <!-- AC-5: reconciliation is a DOCUMENTED MANUAL step — there is no in-app
                 BobGo cancel and a voided Sage invoice is never auto-cleared/re-pushed.
                 The always-visible derived status above IS the reconciliation surface;
                 this note states the operational boundary. Kept outside the fieldset so
                 it stays readable during a push. -->
            <p class="dispatch-panel__reconcile text-muted">
                <i class="mdi mdi-information-outline mr-1" aria-hidden="true"></i>
                Need to cancel or reconcile a dispatch? There is no in-app cancel.
                Cancel a shipment directly with BobGo / the courier — the status above
                stays shown for reference. A Sage invoice voided or deleted in Sage is
                <strong>not</strong> re-pushed automatically; reconcile it in Sage. The
                badges above always reflect exactly what was recorded.
            </p>
        </b-card>

        <b-card v-if="hasMounted" style="min-height: 400px">
            <!-- these compoennts are for printing -->
            <printCustomerLabel v-show="show_printCustomerLabel" />

            <tagPrint v-show="false"></tagPrint>

            <printQualityControlForm v-show="show_printQualityControlForm" />
            <!-- end of printing -->

            <orderTracking v-if="hasMounted" :order_data="selected_order_details" :read_only="!canEditContent" />
        </b-card>

        <!-- <b-overlay no-wrap :show="!hasMounted"></b-overlay> -->
    </div>
</template>

<script>
import OrderTracking from "../ext/track-order.vue";
import { adminComputed, orderComputed, orderMethods, crmMethods } from "@/state/helpers";

//eslint-disable-next-line
import printJS from "print-js";
import * as html2pdf from "../../plugins/html2pdf/html2pdf.bundle.min";
import printCustomerLabel from "./print/customerLabel.vue";
import printQualityControlForm from "./print/qualityControlForm.vue";
import tagPrint from "./print/tagPrint.vue";
import LabelPrintTrigger from "./print/LabelPrintTrigger.vue";
import DispatchAction from "@/components/shared/DispatchAction.vue";
import StatusPill from "@/components/shared/StatusPill.vue";

export default {
    components: {
        OrderTracking,
        printCustomerLabel,
        printQualityControlForm,
        tagPrint,
        LabelPrintTrigger,
        DispatchAction,
        StatusPill
    },
    data: () => ({
        hasMounted: false,
        show_modal: false,
        edit_property_id: -1,
        //these are for the v-show for the printing components
        show_printCustomerLabel: false,
        show_printQualityControlForm: false,
        //Story 7.8: shared busy flag set by BOTH the Ship and Invoice DispatchActions
        //(@busy-change). While any push is in flight every sibling dispatch control is
        //locked — the top-bar print buttons (gated on this flag) plus the whole panel
        //<fieldset> (Ship, Invoice, bulk-label) — mirroring the server-side concurrency
        //guard. DispatchAction always emits busy-change(false) in a finally (success,
        //failure AND timeout), so the lock can never stick.
        dispatchBusy: false,
    }),
    computed: {
        ...adminComputed,
        ...orderComputed,
        canEditContent() {
            if (this.user_role == "admin" || this.user_role == "production") {
                return true;
            }
            return false;
        },
        //Drives the bulk-label button visibility (AC-1). is_bulk is decorated per
        //line item by getOrder (Story 6.3 Task 1). Load-time gate only — the actual
        //printed jobs come from the print-time server re-eval in printBulkLabels().
        hasBulkItems() {
            const o = this.selected_order_details;
            return !!(o && Array.isArray(o.products) && o.products.some((p) => p.is_bulk === true));
        },
        //Story 7.3: the waybill + tracking links shown in the DispatchAction result badge
        //once the order is shipped. Both flow from the committed (decorated) Order.
        shipArtifacts() {
            const o = this.selected_order_details || {};
            //Only surface links that actually have an href — after a partial ship (ref
            //committed but waybill/tracking not yet stored, or a null signed URL) the badge
            //must not render dead links.
            return [
                { href: o.public_waybill_src, label: "Waybill" },
                { href: o.tracking_url, label: "Track" },
            ].filter((a) => !!a.href);
        },
    },
    methods: {
        ...orderMethods,
        ...crmMethods,
        resetOrderData() {
            // this.$router.push({ path: "/clients" });
            //go back to prev route
            this.$router.go(-1);
        },
        isOrderLoaded() {
            // check if no order data exists OR id old order data != the selected order id
            if (this.selected_order_details == null) {
                return false;
            }

            return this.selected_order_details.id == this.selected_order_id;
        },
        printTag() {
            let item = this.selected_order_details;
            let params = {
                type: "order",
                order_no: item.order_no,
                tracking_src: item.tracking_src,
                contact_person: item.contact_person,
                contact_no: item.contact_no,
                client_name: item.client.company_name,
                address: item.delivery_address,
                city: item.city,
                province: item.province,
                postal_code: item.postal_code
            };
            let tagData = [];
            tagData.push(params);
            this.$eventHub.$emit('printTag', tagData);
        },
        printBulkLabels() {
            //AC-3: re-evaluate bulk eligibility server-side at PRINT time. The emitted
            //jobs are built ONLY from this fresh response, never from load-time data.
            //Guard the order having been cleared/replaced between render and click
            //(hasBulkItems gates visibility, but this handler can fire on stale state).
            if (!this.selected_order_details) {
                return;
            }
            const id = this.selected_order_details.id;
            this.getBulkLabelItems(id).then((data) => {
                if (!data || !data.has_bulk || !data.items || !data.items.length) {
                    //Explicit no-bulk → emit nothing, no blank PDF (covers the race
                    //where the flag flipped between page load and this click).
                    this.$bvToast.toast("This order has no bulk items to print.", {
                        title: "No bulk items",
                        variant: "warning",
                        solid: true,
                    });
                    return;
                }
                //One text-only job per bulk line item. Do NOT truncate here —
                //tagPrint truncates (client_name 40 / product_name 40).
                const jobs = data.items.map((it) => ({
                    label_type: "bulk",
                    client_name: it.client_name,
                    product_name: it.product_name,
                }));
                //Reuses the already-mounted hidden <tagPrint> via the global bus.
                this.$eventHub.$emit("printTag", jobs);
            }).catch(() => {
                //The Vuex action rejects on any HTTP failure (network / expired JWT /
                //404 / 500). Surface it instead of silently swallowing — without this
                //the button does nothing and an unhandled rejection fires.
                this.$bvToast.toast("Couldn't load bulk items. Please try again.", {
                    title: "Print failed",
                    variant: "danger",
                    solid: true,
                });
            });
        },
        //both printing buttons will use this method
        printPDF(elementID) {
            const element = document.getElementById(elementID);
            if (!element) {
                //eslint-disable-next-line
                console.error("Element not found");
                return;
            }
            // Pull the brand logo as a same-origin base64 data URI BEFORE rendering
            // so it embeds in the html2canvas/PDF output — a cross-origin S3 URL
            // would render blank. setBrandLogoDataUri never rejects (null -> the
            // print component falls back to the bundled static logo).
            this.setBrandLogoDataUri().then(() => {
                this.generatePDF(elementID, element);
            });
        },
        generatePDF(elementID, element) {
            //will v-show on the screen so that html2pdf can "see" the content to print, we set to false after the pdf is generated and passed to printJS
            elementID == "printCustomerLabel" ? (this.show_printCustomerLabel = true) : (this.show_printQualityControlForm = true);

            const options = {
                margin: [10, 10, 10, 10], // top, left, bottom, right
                //this will be the file name
                filename: "exported.pdf",
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: { scale: 2, logging: true },
                jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            };
            // Extend the options object with the didDrawPage hook
            // options.jsPDF.autoPaging = true;
            // options.jsPDF.didDrawPage = (pdf) => {
            //     // If the current position (pdf.y) is beyond the page's available height,
            //     // insert a page break and reset the y position
            //     if (pdf.y > pdf.internal.pageSize.height - options.margin[2]) {
            //         pdf.addPage();
            //         pdf.y = options.margin[0];
            //     }
            // };

            if (typeof window.chrome !== "undefined") {
                this.$nextTick(() => {
                    html2pdf()
                        .set(options)
                        .from(element)
                        .outputPdf("blob")
                        .then(function (pdfBlob) {
                            const url = URL.createObjectURL(pdfBlob);
                            printJS({
                                printable: url,
                                type: "pdf",
                            });
                        })
                        .catch(function (error) {
                            //eslint-disable-next-line
                            console.error("PDF generation error:", error);
                        });

                    //set so that v-show will be false
                    elementID == "printCustomerLabel" ? (this.show_printCustomerLabel = false) : (this.show_printQualityControlForm = false);
                });
            } else {
                //all other browsers will use this
                this.$nextTick(() => {
                    html2pdf()
                        .set(options)
                        .from(element)
                        .outputPdf("datauristring")
                        .then(function (pdfDataUri) {
                            const win = window.open(pdfDataUri, "_blank");
                            if (!win) {
                                //eslint-disable-next-line
                                console.error("Failed to open window");
                                return;
                            }
                        })
                        .catch(function (error) {
                            //eslint-disable-next-line
                            console.error("PDF generation error:", error);
                        });

                    //set so that v-show will be false
                    elementID == "printCustomerLabel" ? (this.show_printCustomerLabel = false) : (this.show_printQualityControlForm = false);
                });
            }
        },
    },

    mounted() {
        if (this.isOrderLoaded()) {
            this.hasMounted = true;
        } else {
            this.getOrderDetails(this.$route.params.orderid).then(() => {
                this.hasMounted = true;
            });
        }
    },
};
</script>

<style lang="scss" scoped>
.propertyContainer {
    display: flex;
    flex-flow: row wrap;
}
.propertyTile {
    /*background: white;*/
    margin: 0.25rem 0.25rem 0.35rem 0.25rem;
    height: 2.5rem;
    /*width: calc( (100% / 3) - 20px );*/
    width: 12rem;
    padding: 0.5rem;

    /* allow floating top right icon */
    position: relative;
}
.page-break {
    display: block;
    page-break-before: always;
}
.avoid-page-break {
    display: table;
    width: 100%;
}
.avoid-page-break tbody {
    display: table-row-group;
}
.avoid-inside-page-break {
    page-break-inside: avoid;
}

/* Story 7.8 Dispatch panel: one row per dispatch step, each pairing a step label
   with its derived StatusPill + DispatchAction so half-states read at a glance. */
.dispatch-panel__controls {
    /* reset native fieldset chrome — used only for the disabled-group semantics */
    border: 0;
    margin: 0;
    padding: 0;
    min-width: 0;
}
.dispatch-panel__row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 0.5rem 0;
}
.dispatch-panel__row + .dispatch-panel__row {
    border-top: 1px solid var(--c-border, #eff2f7);
}
.dispatch-panel__step {
    min-width: 9rem;
    font-weight: 600;
}
.dispatch-panel__reconcile {
    margin-top: 0.75rem;
    margin-bottom: 0;
    font-size: 0.8125rem;
}
</style>
